import { supabase, sessionHelpers } from './supabaseClient'

/**
 * Service untuk mengelola riwayat hasil agent dengan auto-delete setelah 30 hari
 */
export class AgentHistoryService {
  /**
   * Helper method to validate user session and get authenticated user
   * @returns {Promise<Object>} User object if authenticated
   * @throws {Error} If user is not authenticated or session is invalid
   */
  static async getAuthenticatedUser() {
    try {
      const { valid, session, error } = await sessionHelpers.validateSession()
      
      if (!valid) {
        const errorMessage = error?.message || error || 'Authentication failed'
        console.error('Session validation failed:', { valid, session, error })
        throw new Error(`User not authenticated: ${errorMessage}. Please log in again.`)
      }
      
      if (!session || !session.user) {
        console.error('No session or user found:', { session })
        throw new Error('User not authenticated. Please log in again.')
      }
      
      // Log session info for debugging
      console.log('User authenticated successfully:', {
        userId: session.user.id,
        email: session.user.email,
        hasAccessToken: !!session.access_token
      })
      
      return session.user
    } catch (error) {
      console.error('Authentication error:', error)
      throw error
    }
  }
  /**
   * Menyimpan hasil agent baru
   * @param {Object} agentData - Data hasil agent
   * @param {string} agentData.title - Judul hasil agent
   * @param {string} agentData.query - Query asli yang digunakan
   * @param {string} agentData.agentType - Tipe agent: 'skripsi_agent', 'artikel_agent', 'makalah_agent', 'gambar_agent'
   * @param {string} agentData.agentId - ID agent yang digunakan
   * @param {Object} agentData.content - Konten lengkap hasil agent
   * @param {Array} agentData.sources - Sumber referensi yang digunakan
   * @param {Object} agentData.metadata - Metadata tambahan (outline, progress, dll)
   * @param {string} agentData.status - Status: 'completed', 'failed', 'in_progress'
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async saveAgentResult(agentData) {
    try {
      const user = await this.getAuthenticatedUser()

      // Check for recent duplicates (within last 5 minutes)
      let timeThreshold, existingRecords
      
      if (false) {
        // Legacy code removed
        const twoMinutesAgo = new Date(Date.now() - 2 * 60 * 1000).toISOString()
        const { data } = await supabase
          .from('agent_history')
          .select('id, query, agent_type, created_at, content')
          .eq('user_id', user.id)
          .eq('agent_type', agentData.agentType)
          .eq('query', agentData.query)
          .gte('created_at', twoMinutesAgo)
          .order('created_at', { ascending: false })
          .limit(1)
        
        // Additional check for app name to ensure it's the same project
        existingRecords = data?.filter(record => 
          record.content?.appConfig?.name === agentData.content?.appConfig?.name
        ) || []
      } else {
        const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString()
        const { data, error: duplicateError } = await supabase
          .from('agent_history')
          .select('id, query, agent_type, created_at, title')
          .eq('user_id', user.id)
          .eq('agent_type', agentData.agentType)
          .eq('query', agentData.query)
          .gte('created_at', tenMinutesAgo)
          .order('created_at', { ascending: false })
          .limit(3)
        
        if (duplicateError) {
          console.error('Duplicate detection query error:', {
            error: duplicateError,
            errorCode: duplicateError.code,
            errorMessage: duplicateError.message,
            userId: user.id,
            agentType: agentData.agentType,
            query: agentData.query
          })
          
          // Handle 406 errors in duplicate detection
          if (duplicateError.message?.includes('406') || duplicateError.code === '406') {
            console.warn('Skipping duplicate detection due to authentication error, proceeding with insert')
            existingRecords = []
          } else {
            throw duplicateError
          }
        } else {
          existingRecords = data || []
        }
      }

      // If duplicate found, update the most recent record instead of creating new one
      if (existingRecords && existingRecords.length > 0) {
        // Find the most recent record with similar title or use the first one
        const title = agentData.title || this.generateTitle(agentData.query, agentData.agentType)
        let existingRecord = existingRecords.find(record => 
          record.title === title || 
          record.title.toLowerCase().includes(title.toLowerCase().substring(0, 20))
        ) || existingRecords[0]
        
        console.log('🔄 Updating existing record instead of creating duplicate:', {
          existingId: existingRecord.id,
          existingTitle: existingRecord.title,
          newTitle: title
        })
        
        const { data: updatedData, error: updateError } = await supabase
          .from('agent_history')
          .update({
            title: title, // Update title as well
            content: agentData.content,
            metadata: agentData.metadata,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRecord.id)
          .select()

        if (updateError) {
          console.error('Error updating agent history:', updateError)
          throw updateError
        }

        return { data: updatedData[0], error: null }
      }

      // Generate title jika tidak ada
      const title = agentData.title || this.generateTitle(agentData.query, agentData.agentType)

      console.log('💾 Inserting to agent_history with:', {
        agent_type: agentData.agentType,
        title: title,
        query: agentData.query
      })

      const { data, error } = await supabase
        .from('agent_history')
        .insert({
          user_id: user.id,
          title: title,
          query: agentData.query,
          agent_type: agentData.agentType,
          agent_id: agentData.agentId,
          content: agentData.content || {},
          sources: agentData.sources || [],
          metadata: agentData.metadata || {},
          status: agentData.status || 'completed',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()

      if (data && data[0]) {
        console.log('✅ Successfully saved to agent_history:', {
          id: data[0].id,
          agent_type: data[0].agent_type,
          title: data[0].title
        })
      }

      if (error) throw error

      return { data: data[0], error: null }
    } catch (error) {
      console.error('Error saving agent result:', error)
      return { data: null, error: error.message }
    }
  }

  /**
   * Update hasil agent yang sudah ada
   * @param {string} historyId - ID history
   * @param {Object} updateData - Data yang akan diupdate
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async updateAgentResult(historyId, updateData) {
    try {
      const user = await this.getAuthenticatedUser()

      const updatePayload = {
        ...updateData,
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('agent_history')
        .update(updatePayload)
        .eq('id', historyId)
        .eq('user_id', user.id)
        .select()

      if (error) throw error

      return { data: data[0], error: null }
    } catch (error) {
      console.error('Error updating agent result:', error)
      return { data: null, error: error.message }
    }
  }

  /**
   * Mendapatkan daftar riwayat agent dengan pagination dan filter
   * @param {Object} options - Opsi query
   * @param {string} options.agentType - Filter berdasarkan tipe agent
   * @param {string} options.status - Filter berdasarkan status
   * @param {number} options.limit - Jumlah item per halaman
   * @param {number} options.offset - Offset untuk pagination
   * @param {string} options.orderBy - Field untuk sorting
   * @param {string} options.orderDirection - Arah sorting (asc/desc)
   * @returns {Promise<Object>} Result dengan data, total, dan error
   */
  static async getAgentHistory(options = {}) {
    try {
      const user = await this.getAuthenticatedUser()

      const {
        agentType = 'all',
        status = 'all',
        limit = 20,
        offset = 0,
        orderBy = 'updated_at',
        orderDirection = 'desc'
      } = options

      let query = supabase
        .from('agent_history')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)

      // Apply filters
      if (agentType !== 'all') {
        query = query.eq('agent_type', agentType)
      }

      if (status !== 'all') {
        query = query.eq('status', status)
      }

      // Apply sorting and pagination
      query = query
        .order(orderBy, { ascending: orderDirection === 'asc' })
        .range(offset, offset + limit - 1)

      const { data, error, count } = await query

      if (error) {
        console.error('Supabase query error:', {
          error,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
          userId: user.id,
          queryParams: { agentType, status, limit, offset, orderBy, orderDirection }
        })
        
        // Handle specific 406 errors
        if (error.message?.includes('406') || error.code === '406') {
          throw new Error('API authentication error (406). Please refresh the page and try again.')
        }
        
        throw error
      }

      return { data: data || [], total: count || 0, error: null }
    } catch (error) {
      console.error('Error fetching agent history:', error)
      return { data: [], total: 0, error: error.message }
    }
  }

  /**
   * Mendapatkan detail lengkap dari satu riwayat agent
   * @param {string} historyId - ID history
   * @returns {Promise<Object>} Result dengan data atau error
   */
  static async getAgentHistoryDetail(historyId) {
    try {
      const user = await this.getAuthenticatedUser()

      const { data, error } = await supabase
        .from('agent_history')
        .select('*')
        .eq('id', historyId)
        .eq('user_id', user.id)
        .single()

      if (error) {
        console.error('Supabase getAgentHistoryDetail error:', {
          error,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
          historyId,
          userId: user.id
        })
        
        // Handle specific 406 errors
        if (error.message?.includes('406') || error.code === '406') {
          throw new Error('API authentication error (406). Please refresh the page and try again.')
        }
        
        throw error
      }

      // Update last accessed time
      await this.updateAgentResult(historyId, {})

      return { data, error: null }
    } catch (error) {
      console.error('Error fetching agent history detail:', error)
      return { data: null, error: error.message }
    }
  }

  /**
   * Menghapus riwayat agent
   * @param {string} historyId - ID history
   * @returns {Promise<Object>} Result dengan error jika ada
   */
  static async deleteAgentHistory(historyId) {
    try {
      const user = await this.getAuthenticatedUser()

      const { error } = await supabase
        .from('agent_history')
        .delete()
        .eq('id', historyId)
        .eq('user_id', user.id)

      if (error) throw error

      return { error: null }
    } catch (error) {
      console.error('Error deleting agent history:', error)
      return { error: error.message }
    }
  }

  /**
   * Membersihkan duplikasi dalam agent history
   * Menghapus duplikasi berdasarkan query dan agent_type yang sama, hanya menyisakan yang terbaru
   * @returns {Promise<Object>} Result dengan jumlah yang dihapus atau error
   */
  static async cleanupDuplicates() {
    try {
      const user = await this.getAuthenticatedUser()

      // Get all records with content for better duplicate detection
      const { data: allRecords, error: fetchError } = await supabase
        .from('agent_history')
        .select('id, query, agent_type, created_at, title, content')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      // Group records by query + agent_type
      const groupedRecords = {}
      allRecords.forEach(record => {
        const key = `${record.query}_${record.agent_type}`
        
        if (!groupedRecords[key]) {
          groupedRecords[key] = []
        }
        groupedRecords[key].push(record)
      })

      // Find duplicates to delete (keep only the latest one)
      const idsToDelete = []
      Object.values(groupedRecords).forEach(group => {
        if (group.length > 1) {
          // Sort by created_at descending
          const sortedGroup = group.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          
          // For writing_agent, also check for content similarity
          if (group[0].agent_type === 'writing_agent') {
            const contentGroups = {}
            sortedGroup.forEach(record => {
              const contentKey = record.content ? 
                JSON.stringify(record.content).substring(0, 100) : 'empty'
              
              if (!contentGroups[contentKey]) {
                contentGroups[contentKey] = []
              }
              contentGroups[contentKey].push(record)
            })
            
            // For each content group, keep only the latest
            Object.values(contentGroups).forEach(contentGroup => {
              if (contentGroup.length > 1) {
                const sortedContentGroup = contentGroup.sort((a, b) => 
                  new Date(b.created_at) - new Date(a.created_at)
                )
                const duplicates = sortedContentGroup.slice(1)
                idsToDelete.push(...duplicates.map(record => record.id))
              }
            })
          } else {
            // For other agents, keep only the latest by time
            const duplicates = sortedGroup.slice(1)
            idsToDelete.push(...duplicates.map(record => record.id))
          }
        }
      })

      if (idsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('agent_history')
          .delete()
          .in('id', idsToDelete)
          .eq('user_id', user.id)

        if (deleteError) throw deleteError

        console.log(`🧹 Cleaned up ${idsToDelete.length} duplicate records`)
        return { deletedCount: idsToDelete.length, error: null }
      }

      return { deletedCount: 0, error: null }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error)
      return { deletedCount: 0, error: error.message }
    }
  }

  /**
   * Menghapus semua riwayat agent user
   * @returns {Promise<Object>} Result dengan error jika ada
   */
  static async clearAllAgentHistory() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User not authenticated')
      }

      const { error } = await supabase
        .from('agent_history')
        .delete()
        .eq('user_id', user.id)

      if (error) throw error

      return { error: null }
    } catch (error) {
      console.error('Error clearing agent history:', error)
      return { error: error.message }
    }
  }

  /**
   * Mencari dalam riwayat agent
   * @param {string} searchTerm - Term pencarian
   * @param {Object} options - Opsi pencarian
   * @returns {Promise<Object>} Result dengan data, total, dan error
   */
  static async searchInAgentHistory(searchTerm, options = {}) {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User not authenticated')
      }

      const {
        limit = 20,
        offset = 0,
        orderBy = 'last_accessed',
        orderDirection = 'desc'
      } = options

      const { data, error, count } = await supabase
        .from('agent_history')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id)
        .or(`title.ilike.%${searchTerm}%,query.ilike.%${searchTerm}%`)
        .order(orderBy, { ascending: orderDirection === 'asc' })
        .range(offset, offset + limit - 1)

      if (error) throw error

      return { data: data || [], total: count || 0, error: null }
    } catch (error) {
      console.error('Error searching agent history:', error)
      return { data: [], total: 0, error: error.message }
    }
  }

  /**
   * Membersihkan riwayat agent yang sudah lama (30 hari)
   * @returns {Promise<Object>} Result dengan error jika ada
   */
  static async cleanupOldAgentHistory() {
    try {
      const thirtyDaysAgo = new Date()
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)

      const { error } = await supabase
        .from('agent_history')
        .delete()
        .lt('created_at', thirtyDaysAgo.toISOString())

      if (error) throw error

      return { error: null }
    } catch (error) {
      console.error('Error cleaning up old agent history:', error)
      return { error: error.message }
    }
  }

  /**
   * Generate title dari query dan agent type
   * @param {string} query - Query asli
   * @param {string} agentType - Tipe agent
   * @returns {string} Generated title
   */
  static generateTitle(query, agentType) {
    const maxLength = 100
    const agentTypeLabels = {
      'skripsi_agent': 'Skripsi',
      'artikel_agent': 'Artikel',
      'makalah_agent': 'Makalah',
      'gambar_agent': 'Gambar',
      'video_agent': 'Video',
      'berita_agent': 'Berita',
      'buku_agent': 'Buku',
      'search_agent': 'Search',
      'trending_agent': 'Trending',
      'writing_agent': 'Writing',
      'ppt_agent': 'PPT',
      'chart_agent': 'Chart',
      'research_agent': 'Research',
      'pdf_agent': 'PDF',
      'research': 'Research' // Handle database value
    }
    
    const typeLabel = agentTypeLabels[agentType] || 'Agent'
    const baseTitle = `${typeLabel}: ${query}`
    
    return baseTitle.length > maxLength 
      ? baseTitle.substring(0, maxLength - 3) + '...'
      : baseTitle
  }

  /**
   * Mendapatkan statistik riwayat agent
   * @returns {Promise<Object>} Result dengan data statistik
   */
  static async getAgentHistoryStats() {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      if (!user) {
        throw new Error('User not authenticated')
      }

      // Get total count
      const { count: total } = await supabase
        .from('agent_history')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)

      // Get count by agent type
      const { data: agentTypeCounts } = await supabase
        .from('agent_history')
        .select('agent_type')
        .eq('user_id', user.id)

      const stats = {
        total: total || 0,
        skripsi_agent: 0,
        artikel_agent: 0,
        makalah_agent: 0,
        gambar_agent: 0,
        video_agent: 0,
        berita_agent: 0,
        buku_agent: 0,
        novel_agent: 0,
        search_agent: 0,
        trending_agent: 0,
        writing_agent: 0,
        ppt_agent: 0,
        chart_agent: 0,
        research_agent: 0,
        pdf_agent: 0
      }

      if (agentTypeCounts) {
        agentTypeCounts.forEach(item => {
          // Handle both frontend (research_agent) and database (research) values
          let agentType = item.agent_type
          if (agentType === 'research') {
            agentType = 'research_agent'
          }
          
          if (stats.hasOwnProperty(agentType)) {
            stats[agentType]++
          }
        })
      }

      return { data: stats, error: null }
    } catch (error) {
      console.error('Error fetching agent history stats:', error)
      return { data: { total: 0, skripsi_agent: 0, artikel_agent: 0, makalah_agent: 0, gambar_agent: 0, video_agent: 0, berita_agent: 0, buku_agent: 0, novel_agent: 0, search_agent: 0, trending_agent: 0, writing_agent: 0, ppt_agent: 0, chart_agent: 0, research_agent: 0, pdf_agent: 0 }, error: error.message }
    }
  }
}

export default AgentHistoryService